// Helpers for cargo overweight / capacity checks during ML sheet creation.
//
// Weight semantics:
//   - Shipment-level `weight` holds the summed chargeable weight (kg) for cargo bookings.
//   - Fallback: sum of parcel `chargeable_weight`.
//   - Vehicle capacity is stored in metric tons (MT); `capacity_kg` = capacity * 1000.
//   - Same-day same-vehicle: `alreadyLoadedKg` reduces available capacity.

export const getShipmentWeight = (shipment) => {
  if (!shipment) return 0;
  const w = Number(shipment.weight);
  if (Number.isFinite(w) && w > 0) return w;
  const parcels = shipment.parcels || [];
  return parcels.reduce((sum, p) => sum + (Number(p.chargeable_weight) || 0), 0);
};

export const getVehicleCapacityKg = (vehicle) => {
  if (!vehicle) return null;
  let cap = null;
  if (vehicle.capacity_kg != null) cap = Number(vehicle.capacity_kg);
  else if (vehicle.capacity != null) cap = Number(vehicle.capacity) * 1000;
  return Number.isFinite(cap) && cap > 0 ? cap : null;
};

/**
 * Evaluate proposed shipment weight against vehicle capacity minus same-day load.
 *
 * @param {object} opts
 * @param {object} opts.vehicle
 * @param {array}  opts.shipments - shipments being added/kept on THIS sheet
 * @param {number} [opts.alreadyLoadedKg=0] - weight already on other same-day MLs for this vehicle
 */
export const evaluateVehicleLoad = ({ vehicle, shipments, alreadyLoadedKg = 0 }) => {
  const capacityKg = getVehicleCapacityKg(vehicle);
  const rows = (shipments || []).map((s) => ({
    shipment_id: s.shipment_id || s.id,
    weight: getShipmentWeight(s),
  }));
  const newWeight = Math.round(rows.reduce((sum, r) => sum + r.weight, 0));
  const already = Math.round(Math.max(0, Number(alreadyLoadedKg) || 0));
  const totalWeight = Math.round(newWeight + already);

  if (!capacityKg) {
    return {
      hasCapacity: false,
      overweight: false,
      capacityKg: null,
      alreadyLoadedKg: already,
      availableKg: null,
      newWeight,
      totalWeight,
      excessKg: 0,
      extraShipmentIds: [],
    };
  }

  const availableKg = Math.round(Math.max(0, capacityKg - already));
  const overweight = totalWeight > capacityKg;
  const extraShipmentIds = [];
  if (overweight) {
    // Suggest removing the heaviest NEW shipments first until the rest fits available.
    const sorted = [...rows].sort((a, b) => b.weight - a.weight);
    let running = newWeight;
    for (const r of sorted) {
      if (running <= availableKg) break;
      extraShipmentIds.push(r.shipment_id);
      running -= r.weight;
    }
  }

  return {
    hasCapacity: true,
    overweight,
    capacityKg: Math.round(capacityKg),
    alreadyLoadedKg: already,
    availableKg,
    newWeight,
    totalWeight,
    excessKg: overweight ? Math.round(totalWeight - capacityKg) : 0,
    extraShipmentIds,
  };
};

export const formatKg = (value) => `${Math.round(Number(value) || 0).toLocaleString('en-IN')} kg`;
